import React, { useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

const SectionBackground = ({ variant = 'default' }) => {
  const containerRef = useRef()

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const ctx = gsap.context(() => {
      // Animate gradient blobs
      const blobs = container.querySelectorAll('.gradient-blob')
      blobs.forEach((blob, i) => {
        gsap.to(blob, {
          x: `random(-100, 100)`,
          y: `random(-100, 100)`,
          scale: `random(0.8, 1.4)`,
          duration: `random(15, 25)`,
          ease: 'sine.inOut',
          repeat: -1,
          yoyo: true,
          delay: i * 2,
        })
      })

      // Animate particles
      const particles = container.querySelectorAll('.particle')
      particles.forEach((particle) => {
        gsap.to(particle, {
          y: `random(-200, 200)`,
          x: `random(-100, 100)`,
          opacity: `random(0.2, 0.8)`,
          duration: `random(20, 40)`,
          ease: 'none',
          repeat: -1,
          yoyo: true,
        })
      })

      // Scroll-based color transition
      gsap.to(container, {
        scrollTrigger: {
          trigger: container,
          start: 'top 80%',
          end: 'bottom 20%',
          scrub: 1,
        },
        opacity: 1,
      })
    })

    return () => ctx.revert()
  }, [])

  const backgrounds = {
    hero: (
      <div ref={containerRef} className="absolute inset-0 -z-10 overflow-hidden bg-gradient-to-br from-navy-lightest via-white to-navy-pale dark:from-navy-dark dark:via-navy-deep dark:to-navy-base/30">
        {/* Animated gradient blobs (neutral in dark mode) */}
        <div className="gradient-blob absolute top-20 left-10 w-96 h-96 bg-navy-medium/30 dark:bg-github-border/30 rounded-full blur-3xl"></div>
        <div className="gradient-blob absolute top-40 right-20 w-[500px] h-[500px] bg-accent/20 dark:bg-github-border/20 rounded-full blur-3xl"></div>
        <div className="gradient-blob absolute bottom-20 left-1/3 w-[450px] h-[450px] bg-mint/20 dark:bg-github-border/20 rounded-full blur-3xl"></div>
        
        {/* Floating particles */}
        <div className="absolute inset-0">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="particle absolute w-2 h-2 bg-navy-medium/40 dark:bg-github-border/30 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
            />
          ))}
        </div>
      </div>
    ),
    
    about: (
      <div ref={containerRef} className="absolute inset-0 -z-10 overflow-hidden bg-gradient-to-br from-white via-navy-pale/30 to-white dark:from-navy-dark dark:via-navy-deep dark:to-navy-dark">
        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.05] dark:opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(48, 54, 61, 0.35) 1px, transparent 1px),
                             linear-gradient(90deg, rgba(48, 54, 61, 0.35) 1px, transparent 1px)`,
            backgroundSize: '50px 50px',
          }}
        />
        
        {/* Radial gradients */}
        <div className="gradient-blob absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_left,rgba(48,54,61,0.25),transparent_55%)]"></div>
        <div className="gradient-blob absolute bottom-0 right-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_right,rgba(48,54,61,0.2),transparent_55%)]"></div>
        
        {/* Floating particles */}
        <div className="absolute inset-0">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="particle absolute w-3 h-3 border-2 border-navy-medium/20 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
            />
          ))}
        </div>
      </div>
    ),
    
    education: (
      <div ref={containerRef} className="absolute inset-0 -z-10 overflow-hidden bg-gradient-to-br from-navy-pale/40 via-white to-mint/10 dark:from-navy-dark dark:via-navy-deep dark:to-navy-dark">
        {/* Diagonal lines */}
        <div className="absolute inset-0 opacity-[0.03] dark:opacity-[0.02]">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="diagonalLines" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                <line x1="0" y1="0" x2="20" y2="20" stroke="rgba(48, 54, 61, 0.6)" strokeWidth="1"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#diagonalLines)" />
          </svg>
        </div>
        
        {/* Gradient orbs */}
        <div className="gradient-blob absolute top-1/4 right-10 w-80 h-80 bg-navy-medium/20 dark:bg-github-border/25 rounded-full blur-3xl"></div>
        <div className="gradient-blob absolute bottom-1/4 left-10 w-80 h-80 bg-mint/20 dark:bg-github-border/20 rounded-full blur-3xl"></div>
      </div>
    ),
    
    projects: (
      <div ref={containerRef} className="absolute inset-0 -z-10 overflow-hidden bg-gradient-to-br from-navy-lightest via-white to-navy-deep/10 dark:from-navy-deep dark:via-navy-dark dark:to-navy-deep">
        {/* Hexagonal pattern */}
        <div 
          className="absolute inset-0 opacity-[0.04] dark:opacity-[0.04]"
          style={{
            backgroundImage: `radial-gradient(circle, rgba(48, 54, 61, 0.35) 2px, transparent 2px)`,
            backgroundSize: '40px 40px',
          }}
        />
        
        {/* Moving gradient blobs */}
        <div className="gradient-blob absolute top-20 left-20 w-96 h-96 bg-gradient-to-br from-github-border/20 to-transparent rounded-full blur-3xl"></div>
        <div className="gradient-blob absolute bottom-20 right-20 w-96 h-96 bg-gradient-to-br from-github-border/20 to-transparent rounded-full blur-3xl"></div>
        
        {/* Geometric shapes */}
        <div className="absolute top-20 left-20 w-40 h-40 border border-navy-medium/10 rotate-45 animate-spin-slow"></div>
        <div className="absolute bottom-20 right-20 w-32 h-32 border border-accent/10 rotate-12 animate-spin-slower"></div>
      </div>
    ),
    
    blog: (
      <div className="absolute inset-0 -z-10 overflow-hidden">
        {/* Wave pattern */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.03]">
          <defs>
            <pattern id="wave" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
              <path d="M0 50 Q25 25, 50 50 T100 50" stroke="rgba(139, 92, 246, 0.5)" fill="none" strokeWidth="2"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#wave)" />
        </svg>
        
        {/* Gradient spots */}
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl"></div>
      </div>
    ),
    
    resume: (
      <div className="absolute inset-0 -z-10 overflow-hidden">
        {/* Circuit board pattern */}
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `
              linear-gradient(90deg, rgba(139, 92, 246, 0.3) 1px, transparent 1px),
              linear-gradient(rgba(139, 92, 246, 0.3) 1px, transparent 1px),
              linear-gradient(90deg, rgba(139, 92, 246, 0.3) 2px, transparent 2px),
              linear-gradient(rgba(139, 92, 246, 0.3) 2px, transparent 2px)
            `,
            backgroundSize: '20px 20px, 20px 20px, 100px 100px, 100px 100px',
          }}
        />
        
        {/* Glowing corners */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-purple-500/10 blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-64 h-64 bg-pink-500/10 blur-3xl"></div>
      </div>
    ),
    
    contact: (
      <div className="absolute inset-0 -z-10 overflow-hidden">
        {/* Mesh gradient */}
        <div className="absolute inset-0 bg-gradient-to-b from-purple-500/5 to-pink-500/5"></div>
        
        {/* Animated circles */}
        <div className="absolute top-10 left-10 w-48 h-48 border-2 border-primary/10 rounded-full animate-ping-slow"></div>
        <div className="absolute bottom-10 right-10 w-64 h-64 border-2 border-secondary/10 rounded-full animate-ping-slower"></div>
        
        {/* Dots grid */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `radial-gradient(circle, rgba(139, 92, 246, 0.5) 2px, transparent 2px)`,
            backgroundSize: '40px 40px',
          }}
        />
      </div>
    ),
  }

  return backgrounds[variant] || backgrounds.default
}

export default SectionBackground
